import {
  AccountCircle as AccountCircleIcon,
  ApartmentOutlined,
  AutoAwesome as AutoAwesomeIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  FeedbackOutlined as FeedbackOutlinedIcon,
  GridViewOutlined as GridViewOutlinedIcon,
  Menu as MenuIcon,
  MoreVert as MoreVertIcon,
} from "@/components/icons";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { Link as RouterLink, Outlet, useLocation, useNavigate } from "react-router-dom";

import { useTranslation } from "react-i18next";

import { useActiveOrgBrand } from "@/theme/orgBrand";
import { needsRing, onColor } from "@/theme/contrast";

import { BillingStatusChip } from "@/features/admin/BillingStatusChip";
import { AccountMenuItems } from "@/layouts/AccountMenuItems";
import { MaintenanceBanner } from "@/layouts/MaintenanceBanner";
import { NotificationBell } from "@/features/notifications/NotificationBell";
import { VersionBadge } from "@/layouts/VersionBadge";
import { CollapsedSessionRail } from "@/features/sessions/CollapsedSessionRail";
import { openKeyboardShortcuts } from "@/features/help/KeyboardShortcuts";
import { AddonMemberNotice } from "@/features/support/AddonMemberNotice";
import { ChatWithUsButton } from "@/features/support/ChatWithUsButton";
import { recheck as recheckIntercom, shutdownIntercom, useIntercomRouteUpdates } from "@/features/support/useIntercomMessenger";
import { FeedbackDialog } from "@/features/feedback/FeedbackDialog";
import { AskPanel } from "@/features/ask/AskPanel";
import { useAskPanelState } from "@/features/ask/askEvents";
import { useFeedbackConfig } from "@/api/feedback";
import { SessionList } from "@/features/sessions/SessionList";
import { OnboardingAutoShow } from "@/features/onboarding/OnboardingAutoShow";
import { OnboardingNudge } from "@/features/onboarding/OnboardingNudge";
import { useSetupNudge } from "@/features/onboarding/useSetupNudge";
import { isAdminPath, rememberSessionsPath } from "@/features/admin/nav/adminPaths";
import { orgDisplayName } from "@/features/admin/nav/orgDisplay";
import { useWhatsNewUnread } from "@/features/whatsNew/useWhatsNew";
import { WhatsNewController } from "@/features/whatsNew/WhatsNewController";
import { useLogout, useMe } from "@/api/hooks";
import { OrgProvider, useActiveOrg } from "@/features/admin/orgContext";
import { useSessionEventStream } from "@/hooks/useSessionEventStream";
import { useUiLanguageSync } from "@/i18n/useUiLanguage";
import { brand } from "@/theme";
import { repoTokens } from "@/theme/tokens";
import { shellGap, shellPanelSx } from "@/layouts/shellPanels";
import { ShellCollapseButton } from "@/layouts/ShellCollapseButton";
import { ShellRightColumn } from "@/layouts/ShellRightColumn";
import { RightColumnContext, useRightColumnSlot } from "@/layouts/rightColumnSlot";
import {
  clampSidebarWidth,
  readSidebarWidth,
  saveSidebarWidth,
  type SidebarMode,
} from "@/layouts/sidebarWidths";

// The admin menu replaces the session list on /app/admin/*. Loaded on
// demand, so a member's first download doesn't grow by a menu they
// never see.
const AdminSidebarNav = React.lazy(() =>
  import("@/features/admin/nav/AdminSidebarNav").then((m) => ({ default: m.AdminSidebarNav })),
);
const AdminNavRail = React.lazy(() =>
  import("@/features/admin/nav/AdminNavRail").then((m) => ({ default: m.AdminNavRail })),
);

const shell = repoTokens.shell;
/** The collapsed rail: a narrow panel of session avatars or admin icons. */
const COLLAPSED_WIDTH = 56;
/** The Select can't carry ``null``; "All organisations" rides a sentinel. */
const ALL_ORGS = "__all__";
/** The page's ``main``, where the skip link sends focus. */
const MAIN_ID = "app-main";
const STORAGE_COLLAPSED = "repoworks.sidebar.collapsed";

/** Collapsed or not is one choice for the whole app; the width is kept
 *  per content (``sidebarWidths.ts``), and ``mode`` picks the one in use.
 *  The drawer's width transition animates the switch between them. */
function useSidebarLayout(mode: SidebarMode) {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_COLLAPSED) === "1";
    } catch {
      return false;
    }
  });
  const [widths, setWidths] = useState<Record<SidebarMode, number>>(() => ({
    sessions: readSidebarWidth("sessions"),
    admin: readSidebarWidth("admin"),
  }));
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_COLLAPSED, collapsed ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [collapsed]);
  const width = widths[mode];
  const beginResize = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      const startX = event.clientX;
      const startWidth = width;
      let last = width;
      setDragging(true);
      const onMove = (e: MouseEvent) => {
        const next = clampSidebarWidth(mode, startWidth + (e.clientX - startX));
        last = next;
        setWidths((prev) => ({ ...prev, [mode]: next }));
      };
      const onUp = () => {
        setDragging(false);
        // Stored on a drag only: an untouched column follows the default.
        if (last !== startWidth) saveSidebarWidth(mode, last);
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
        document.body.style.userSelect = "";
      };
      document.body.style.userSelect = "none";
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [width, mode],
  );

  const toggle = useCallback(() => setCollapsed((v) => !v), []);
  const effectiveWidth = collapsed ? COLLAPSED_WIDTH : width;

  return { width, effectiveWidth, collapsed, toggle, beginResize, dragging };
}

export function AppShell(): React.JSX.Element {
  // ``OrgProvider`` wraps the whole authenticated app so the
  // session-wide org selector in the AppBar drives every consumer
  // (sessions list, document library, cost overview, admin tabs).
  // Pre-Phase-F the provider was scoped to AdminLayout — non-admin
  // views couldn't see the active-org context at all.
  return (
    <OrgProvider>
      <AppShellBody />
    </OrgProvider>
  );
}

function AppShellBody(): React.JSX.Element {
  const me = useMe();
  // The right column slot: the session view portals its panels into it.
  const {
    slot: rightColumnSlot,
    setHost: setRightColumnHost,
    claimed: rightColumnClaimed,
  } = useRightColumnSlot();
  // The UI language follows the account across devices (and a pick
  // made on the login page is saved to it).
  useUiLanguageSync();
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();
  // Capability-gated, not global-role-gated: org admins/managers
  // get the /app/admin entry too (AdminLayout itself scopes which
  // tabs they see).
  const isAdmin = Boolean(me.data?.admin_scope?.can_access_admin);
  // In admin, the sidebar (rail, phone drawer) is the admin menu. A
  // member who opens an admin URL keeps the session list; the page
  // itself tells them they have no access.
  const onAdminRoute = isAdminPath(location.pathname);
  const adminNav = isAdmin && onAdminRoute;
  // Width by route while /auth/me loads, so a direct admin load doesn't
  // open at the session list's width and then shrink.
  const sidebar = useSidebarLayout(
    adminNav || (onAdminRoute && me.isLoading) ? "admin" : "sessions",
  );
  const { t } = useTranslation();
  const theme = useTheme();
  // Below md, the permanent side rail would steal more than half a
  // phone's viewport; swap to a temporary overlay drawer opened via
  // a hamburger in the AppBar. The desktop path is unchanged.
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isPhone = useMediaQuery(theme.breakpoints.only("xs"));
  const [mobileOpen, setMobileOpen] = useState(false);
  // Auto-close the overlay on navigation — tapping a session in the
  // list should behave like a menu pick, not a lingering curtain.
  // The setState-in-effect rule wants this on the click handler, but
  // the click goes through React Router <Link> components which we
  // don't own; pathname-driven close is the only reliable hook.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (isMobile) setMobileOpen(false);
  }, [location.pathname, isMobile]);
  // The admin menu is ours, so it reports every pick: tapping the page
  // already open changes no route, and left the drawer over it.
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  // Mobile "more actions" menu — folds the row of AppBar icons
  // (browse / documents / admin / changelog / logout) into a single
  // ``MoreVert`` trigger so phones don't crowd the bar with 5+
  // buttons. Desktop keeps the row visible; the hamburger + bell are
  // always visible, the avatar from sm up.
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const moreOpen = Boolean(moreAnchor);
  // Desktop profile menu, opened from the avatar — carries the same
  // account-related rows as the phone's More menu (AccountMenuItems),
  // so language / admin / What's new / help / sign-out live in one
  // place instead of a row of AppBar icons (FW-37).
  const [profileAnchor, setProfileAnchor] = useState<HTMLElement | null>(null);
  const profileOpen = Boolean(profileAnchor);
  const closeProfileMenu = useCallback(() => setProfileAnchor(null), []);
  // "Send feedback": offered when the submission's organisation has it
  // on (``feedback.enabled``, on by default). Only a PINNED org is named:
  // ``active_org_slug`` is otherwise a display default (often root), and
  // filing a member's feedback there hid it from their own org admin.
  // Unpinned, the server picks the org (the member's own) and the dialog
  // shows and uses what it picked.
  const feedbackScope = me.data?.admin_scope;
  const feedbackConfig = useFeedbackConfig(
    feedbackScope?.active_org_pinned ? (feedbackScope.active_org_slug ?? null) : null,
    Boolean(me.data?.user),
  );
  const canSendFeedback = feedbackConfig.data?.enabled === true;
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const requestsOn = me.data?.features?.requests_enabled === true;
  const ask = useAskPanelState(requestsOn);
  // The feedback button IS the Ask button when requests are on (the Ask
  // panel replaces the feedback dialog). Offered even where the org has
  // feedback off: answers still help; the panel then hides the request
  // and report forms.
  const showFeedbackButton = requestsOn || canSendFeedback;
  const feedbackLabel = requestsOn ? t("ask.open") : t("feedback.open");
  const onFeedbackButton = () => (requestsOn ? ask.openAt("ask") : setFeedbackOpen(true));
  // Same close-on-route-change pattern as the mobile drawer above —
  // <Link>-driven navigation never reaches our click handler.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMoreAnchor(null);
    setProfileAnchor(null);
  }, [location.pathname]);
  // Support chat (the Intercom add-on): the Messenger follows route
  // changes, and goes, with its cookies, once the org turns the add-on
  // off or the user's consent is withdrawn or no longer valid.
  const support = me.data?.features?.addons?.support_chat;
  useIntercomRouteUpdates(location.pathname);
  const chatAllowed = support ? support.org_enabled && support.consent_valid : null;
  useEffect(() => {
    if (chatAllowed === false) shutdownIntercom();
  }, [chatAllowed]);
  // Another org is active: a booted Messenger re-boots with its company
  // (or goes, when that org has no chat). A no-op when it isn't booted.
  const activeOrgSlug = me.data?.admin_scope?.active_org_slug ?? null;
  useEffect(() => {
    void recheckIntercom();
  }, [activeOrgSlug]);
  // "Back to sessions" in the admin menu returns to the session last
  // open in this tab.
  useEffect(() => {
    rememberSessionsPath(location.pathname, location.search);
  }, [location.pathname, location.search]);
  // The setup AFFORDANCE (top-bar Setup icon + the mobile overflow entry) is a
  // LIVE prompt, mirroring the OnboardingNudge: it shows only while the core
  // steps are unfinished AND the user hasn't dismissed the nudge for this org.
  // Once dismissed or complete it disappears — the always-present
  // Admin › Organisations link (OrgsTab) is then the way back to /app/welcome.
  // One state with the banner: dismissing it hides this at once.
  const setup = useSetupNudge();
  const showSetupNudge = setup.visible;
  // Changes the user hasn't been offered yet (the What's new dialog's
  // cached answer): a dot on the entry until they are acknowledged. Only
  // fixes never open the dialog, so this is where those show.
  const unread = useWhatsNewUnread();
  const whatsNewLabel =
    unread > 0 ? t("appbar.whats_new_unread", { count: unread }) : t("appbar.changelog");
  const goTo = (path: string) => () => {
    setMoreAnchor(null);
    navigate(path);
  };
  // The profile menu's own navigate/help/sign-out: closes the menu,
  // then does the thing, same shape as ``goTo`` above for the phone
  // menu.
  const profileNavigate = (path: string) => {
    setProfileAnchor(null);
    navigate(path);
  };
  const profileHelp = () => {
    setProfileAnchor(null);
    openKeyboardShortcuts();
  };
  const profileSignOut = () => {
    setProfileAnchor(null);
    void logout.mutateAsync();
  };

  // Subscribe to the session-event SSE stream once at the shell
  // level so the sidebar list, the currently-viewed session, and
  // any open collaborator views all refresh when mutations land —
  // whether they came from this user or a collaborator on a
  // shared session.
  useSessionEventStream();

  // The left column's content: the session list (or, in admin, the
  // admin menu) over the signed-in user's row. Shared by the desktop
  // panel and the phone drawer.
  const sidebarBody = (
    <Stack sx={{ height: "100%", minHeight: 0 }}>
      {adminNav ? (
        <React.Suspense fallback={<Box sx={{ flex: 1 }} />}>
          <AdminSidebarNav
            skipLink={!isMobile}
            onNavigate={isMobile ? closeMobile : undefined}
          />
        </React.Suspense>
      ) : (
        <SessionList />
      )}
      <Divider />
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          gap: 1.5,
          // The left column's inner margin, so the row lines up with the
          // session list above it.
          p: `${shell.leftColumnPadding}px`,
          // Phones with a home indicator report a bottom inset;
          // keep the row above it (needs viewport-fit=cover).
          pb: `calc(${shell.leftColumnPadding}px + env(safe-area-inset-bottom, 0px))`,
        }}
      >
        {me.data?.user.picture_url ? (
          <Avatar src={me.data.user.picture_url} sx={{ width: 28, height: 28 }} />
        ) : (
          <Avatar sx={{ width: 28, height: 28, bgcolor: brand.primary, color: "primary.contrastText", fontSize: 12 }}>
            {(me.data?.user.display_name ?? "?").slice(0, 1).toUpperCase()}
          </Avatar>
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
            {me.data?.user.display_name ?? "…"}
          </Typography>
          {/* A block: on the caption's inline span noWrap's
              ellipsis does nothing, and a long address ran on past
              the panel. The title has all of it. */}
          <Typography
            variant="caption"
            component="div"
            color="text.secondary"
            noWrap
            title={me.data?.user.email}
          >
            {me.data?.user.email ?? ""}
          </Typography>
        </Box>
      </Stack>
    </Stack>
  );

  // Collapsed (desktop only): a narrow rail of session avatars, or the
  // admin menu's icons.
  const collapsedRail = (
    <Stack sx={{ height: "100%", minHeight: 0 }}>
      {adminNav ? (
        <React.Suspense fallback={<Box sx={{ flex: 1 }} />}>
          <AdminNavRail />
        </React.Suspense>
      ) : (
        <CollapsedSessionRail />
      )}
    </Stack>
  );

  return (
    <RightColumnContext.Provider value={rightColumnSlot}>
    <Box
      sx={{
        display: "flex",
        height: "var(--rw-viewport-height)",
        flexDirection: "column",
        bgcolor: repoTokens.colors.page,
        // The page never scrolls: each panel scrolls inside itself. A
        // positioned root with hidden overflow also holds what is placed
        // against the page (a visually hidden, absolutely positioned
        // label in a scrolled panel made the whole page 4,500px tall,
        // with a window scrollbar beside the right column).
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* The first Tab stop: past the app bar's controls (and the
          sidebar) to the page. The admin menu has its own, past the menu. */}
      <Box
        component="a"
        href={`#${MAIN_ID}`}
        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
          // The content is in the same document; move focus, not the URL.
          e.preventDefault();
          document.getElementById(MAIN_ID)?.focus();
        }}
        sx={{
          position: "fixed",
          left: 8,
          top: 8,
          zIndex: (t) => t.zIndex.drawer + 2,
          px: 1.5,
          py: 0.75,
          borderRadius: 1,
          bgcolor: "background.paper",
          color: "text.primary",
          boxShadow: 2,
          // Visually hidden until focused, then over the app bar. The
          // shared recipe spells its sizes in px (in ``sx`` 1 is 100%).
          // :focus, not :focus-visible: a browser without the latter
          // drops the rule and would show the link at all times.
          "&:not(:focus)": { ...visuallyHidden, clipPath: "inset(50%)" },
        }}
      >
        {t("appbar.skip_to_content")}
      </Box>
      {/* Transparent, in the page's flow: no bar, just the controls on
          the page, 12px from its top and on the panels' outer edges:
          15px from the sides (8px on a phone). */}
      <AppBar
        position="static"
        color="transparent"
        sx={{ bgcolor: "transparent", color: "text.primary" }}
      >
        <Toolbar
          disableGutters
          sx={{
            gap: { xs: 0.5, sm: 2 },
            minHeight: { xs: 48, sm: 48 },
            pt: { xs: 1, md: `${shell.appBarTop}px` },
            px: { xs: 1, md: `${shell.appBarPadding}px` },
          }}
        >
          {isMobile && (
            <IconButton
              color="inherit"
              // No ``edge="start"``: its negative margin pulled the button
              // 12px past the 8px edge the panels below keep.
              onClick={() => setMobileOpen((v) => !v)}
              aria-label={t("appbar.menu", "menu")}
              aria-expanded={mobileOpen}
              aria-controls="app-sidebar"
              sx={{ mr: 0.5 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box
            component={RouterLink}
            to="/app"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              color: "inherit",
              textDecoration: "none",
              minWidth: 0,
            }}
          >
            <Box
              component="img"
              // The tenant's own white logo when one is uploaded — the
              // product shell wears the org's brand, not the vendor's.
              src="/brand/repoworks_logo_white.svg"
              alt="Repoworks"
              // Shrinks (keeping its aspect) instead of overflowing its
              // link: on a phone with the org switcher next to it, the
              // fixed-width wordmark ran about 18px under the switcher.
              sx={{
                height: shell.logoHeight,
                width: "auto",
                maxWidth: { xs: "100%", sm: 160 },
                minWidth: 0,
                objectFit: "contain",
                objectPosition: "left center",
              }}
            />
            {/* Hidden on phones — the logo + product name already fight
                for width next to the avatar + drawer affordances, and
                the same build info now leads the /app/changelog view. */}
            <Box sx={{ display: { xs: "none", sm: "inline-flex" } }}>
              <VersionBadge />
            </Box>
          </Box>
          <Box sx={{ flex: 1 }} />
          {me.data?.user && (
            <Stack
              sx={{
                alignItems: "center",
                lineHeight: 1.15,
                textAlign: "center",
                minWidth: 0,
                px: 1,
                // The header already carries logo + avatar + icon
                // affordances; on phones the display-name/email block
                // crowds them and forces truncation. Hide it on xs —
                // the sidebar + /account page both surface the same
                // identity.
                display: { xs: "none", sm: "flex" },
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                {me.data.user.display_name}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }} noWrap>
                {me.data.user.email}
                {me.data.user.role === "superadmin"
                  ? " · superadmin"
                  : me.data.user.role === "admin"
                    ? " · admin"
                    : ""}
              </Typography>
            </Stack>
          )}
          <Box sx={{ flex: 1, display: { xs: "none", sm: "block" } }} />
          <AppBarOrgSwitcher />
          {/* Desktop: full row of action icons. Mobile: folded into
              the overflow menu below to keep the AppBar uncluttered
              on a phone. The notification bell stays visible at every
              width; the account avatar from sm up (on a phone it moves
              into the overflow menu to make room for the bell). */}
          <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: { md: 0.25 } }}>
            {/* Balance + plan, ahead of the icon row: a running-out
                balance is the one piece of state that silently stops
                work, and onboarding never mentions billing at all.
                Renders nothing when billing is off or the viewer is not
                a manager. */}
            <BillingStatusChip />
            <Tooltip title={t("appbar.browse", "Browse sessions")}>
              <IconButton
                color="inherit"
                onClick={() => navigate("/app/browse")}
                aria-label={t("appbar.browse", "Browse sessions")}
              >
                <GridViewOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("appbar.documents")}>
              <IconButton
                color="inherit"
                onClick={() => navigate("/app/documents")}
                aria-label={t("appbar.documents")}
              >
                <DescriptionOutlinedIcon />
              </IconButton>
            </Tooltip>
            {/* Live setup prompt — a compact top-bar counterpart to the
                OnboardingNudge banner. Same gate (unfinished + not dismissed),
                so it disappears once setup is done or dismissed; the persistent
                fallback then lives under Admin › Organisations. */}
            {showSetupNudge && (
              <Tooltip
                title={t("appbar.setup_steps", {
                  count: setup.incompleteCount,
                  defaultValue_one: "Setup — {{count}} step left",
                  defaultValue_other: "Setup — {{count}} steps left",
                })}
              >
                <IconButton
                  color="inherit"
                  onClick={() => navigate("/app/welcome")}
                  aria-label={t("appbar.setup", "Setup")}
                >
                  <Badge badgeContent={setup.incompleteCount} color="warning">
                    <AutoAwesomeIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}
            {showFeedbackButton && (
              <Tooltip title={feedbackLabel}>
                <IconButton
                  color="inherit"
                  onClick={onFeedbackButton}
                  aria-label={feedbackLabel}
                >
                  <FeedbackOutlinedIcon />
                </IconButton>
              </Tooltip>
            )}
            <ChatWithUsButton support={support} />
          </Box>
          <NotificationBell />
          {/* On a phone the bell takes the avatar's slot: the bar has no
              width to spare at 375 px, so the profile menu's rows move
              into the More menu below (isPhone-gated there). */}
          <Tooltip title={t("appbar.account_menu")}>
            <IconButton
              color="inherit"
              onClick={(e) => setProfileAnchor(e.currentTarget)}
              aria-label={t("appbar.account_menu")}
              aria-haspopup="true"
              aria-expanded={profileOpen}
              aria-controls={profileOpen ? "app-profile-menu" : undefined}
              sx={{ display: { xs: "none", sm: "inline-flex" } }}
            >
              <Badge variant="dot" color="success" invisible={unread === 0}>
                {me.data?.user.picture_url ? (
                  <Avatar src={me.data.user.picture_url} sx={{ width: 28, height: 28 }} />
                ) : (
                  <AccountCircleIcon />
                )}
              </Badge>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={profileAnchor}
            open={profileOpen}
            onClose={closeProfileMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{
              paper: { sx: { minWidth: 260 } },
              list: { id: "app-profile-menu", "aria-label": t("appbar.account_menu") },
            }}
          >
            {/* ``role="none"`` + a real ``<li>``: a plain ``div`` inside the
                ``role="menu"`` list is an accessibility-tree foreign body.
                No display name (an account created without one) shows the
                email as the bold line instead, with no second line. */}
            <Box component="li" role="none" sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" noWrap>
                {me.data?.user.display_name || me.data?.user.email || ""}
              </Typography>
              {me.data?.user.display_name && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {me.data.user.email ?? ""}
                </Typography>
              )}
            </Box>
            <Divider />
            <AccountMenuItems
              isAdmin={isAdmin}
              unread={unread}
              whatsNewLabel={whatsNewLabel}
              onNavigate={profileNavigate}
              onHelp={profileHelp}
              onSignOut={profileSignOut}
              onLanguageDone={closeProfileMenu}
            />
          </Menu>
          {/* Mobile-only overflow trigger — a single ``MoreVert``
              icon replaces the row of IconButtons above. */}
          <Tooltip title={t("appbar.more_actions", "More actions")}>
            <IconButton
              color="inherit"
              onClick={(e) => setMoreAnchor(e.currentTarget)}
              aria-label={
                unread > 0 && isPhone
                  ? t("appbar.more_actions_unread", { count: unread })
                  : t("appbar.more_actions", "More actions")
              }
              aria-haspopup="true"
              aria-expanded={moreOpen}
              sx={{ display: { xs: "inline-flex", md: "none" } }}
            >
              {/* Below sm the profile menu's rows (What's new included)
                  fold into this menu (isPhone-gated below); at sm-md the
                  avatar's own menu already carries What's new, so the
                  dot would double up here. */}
              <Badge variant="dot" color="success" invisible={!(unread > 0 && isPhone)}>
                <MoreVertIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={moreAnchor}
            open={moreOpen}
            onClose={() => setMoreAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{ paper: { sx: { minWidth: 220 } } }}
          >
            <MenuItem onClick={goTo("/app/browse")}>
              <ListItemIcon>
                <GridViewOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t("appbar.browse", "Browse sessions")} />
            </MenuItem>
            <MenuItem onClick={goTo("/app/documents")}>
              <ListItemIcon>
                <DescriptionOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t("appbar.documents")} />
            </MenuItem>
            {/* Live setup prompt — the mobile counterpart of the top-bar
                Setup icon. Same gate: shown only while unfinished + not
                dismissed. Once done/dismissed the persistent link lives under
                Admin › Organisations. */}
            {showSetupNudge && (
              <MenuItem onClick={goTo("/app/welcome")}>
                <ListItemIcon>
                  <AutoAwesomeIcon fontSize="small" color="warning" />
                </ListItemIcon>
                <ListItemText
                  primary={t("appbar.setup", "Setup")}
                  secondary={t("appbar.setup_steps", {
                    count: setup.incompleteCount,
                    defaultValue_one: "{{count}} step left",
                    defaultValue_other: "{{count}} steps left",
                  })}
                />
              </MenuItem>
            )}
            {showFeedbackButton && (
              <MenuItem
                onClick={() => {
                  setMoreAnchor(null);
                  onFeedbackButton();
                }}
              >
                <ListItemIcon>
                  <FeedbackOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={feedbackLabel} />
              </MenuItem>
            )}
            <ChatWithUsButton support={support} asMenuItem onDone={() => setMoreAnchor(null)} />
            {/* Below sm the profile avatar gives its AppBar slot to the
                notification bell, so its menu's rows (account settings,
                admin, What's new, help, language, sign-out) fold in here
                too — the shared AccountMenuItems, so the two can't drift. */}
            {isPhone && (
              <>
                <Divider />
                <AccountMenuItems
                  isAdmin={isAdmin}
                  unread={unread}
                  whatsNewLabel={whatsNewLabel}
                  onNavigate={(path) => goTo(path)()}
                  onHelp={() => {
                    setMoreAnchor(null);
                    openKeyboardShortcuts();
                  }}
                  onSignOut={() => {
                    setMoreAnchor(null);
                    void logout.mutateAsync();
                  }}
                  onLanguageDone={() => setMoreAnchor(null)}
                />
              </>
            )}
          </Menu>
        </Toolbar>
      </AppBar>

      {/* The setup reminder, from md: above the panels and on their outer
          edges, 15px under the app bar. On a phone it is in the centre
          panel (below). */}
      {!isMobile && <OnboardingNudge sx={{ mx: shellGap, mt: shellGap }} />}

      {/* The panels: left column, centre, and (later) a right column,
          15px from the page edge and from each other. Room at the
          bottom for the fixed legal banners while they show
          (RequireAuth sets the variable to their height). */}
      <Box
        sx={{
          display: "flex",
          flex: 1,
          minHeight: 0,
          gap: shellGap,
          p: shellGap,
          pb: {
            xs: `calc(${shell.phoneGap}px + var(--rw-legal-stack-height, 0px))`,
            md: `calc(${shell.gap}px + var(--rw-legal-stack-height, 0px))`,
          },
        }}
      >
        {isMobile ? (
          // Below md the left column is an overlay drawer opened from
          // the app bar's menu button, and the centre takes the width.
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            // The hamburger's aria-controls target.
            slotProps={{ paper: { id: "app-sidebar" } }}
            sx={{
              "& .MuiDrawer-paper": {
                width: "min(86vw, 360px)",
                boxSizing: "border-box",
                // Visible-viewport height, so the footer row clears the
                // browser's bottom toolbar (see fonts.css).
                height: "var(--rw-viewport-height)",
                display: "flex",
                flexDirection: "column",
                overflowX: "hidden",
              },
            }}
          >
            {sidebarBody}
          </Drawer>
        ) : (
          <Box
            id="app-sidebar"
            sx={{
              ...shellPanelSx,
              position: "relative",
              width: sidebar.effectiveWidth,
              flexShrink: 0,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              transition: sidebar.dragging ? "none" : "width 200ms ease",
            }}
          >
            {/* Clips the content to the rounded corners; the panel
                itself doesn't, so the collapse button can sit on its
                edge. */}
            <Stack
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
                borderRadius: "inherit",
              }}
            >
              {sidebar.collapsed ? collapsedRail : sidebarBody}
            </Stack>

            {/* Resize handle — 6px-wide vertical strip hugging the right
                edge. Only while expanded; drag to resize within
                [MIN, MAX] and the choice persists. */}
            {!sidebar.collapsed && (
              <Box
                role="separator"
                aria-orientation="vertical"
                onMouseDown={sidebar.beginResize}
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: 6,
                  height: "100%",
                  cursor: "col-resize",
                  zIndex: 2,
                  "&:hover, &:active": {
                    bgcolor: brand.muted,
                    opacity: 0.3,
                  },
                }}
              />
            )}

            {/* Collapse / expand: a round button sitting half outside
                the panel's right edge, vertically centred, the same
                place in both states. */}
            <ShellCollapseButton
              side="left"
              collapsed={sidebar.collapsed}
              onToggle={sidebar.toggle}
              collapseLabel={t("sidebar.collapse", "Collapse sidebar")}
              expandLabel={t("sidebar.expand", "Expand sidebar")}
              controls="app-sidebar"
            />
          </Box>
        )}
        <Box
          component="main"
          id={MAIN_ID}
          // Focus lands here from the skip link, and when the What's new
          // dialog closes on a page where nothing else had focus.
          tabIndex={-1}
          // The setup reminder shows above the panels: a page that has to
          // fit the window (the start page) tightens for it.
          data-setup-nudge={showSetupNudge && !isMobile ? "" : undefined}
          sx={{
            ...shellPanelSx,
            "&:focus": { outline: "none" },
            flex: 1,
            // A page that marks its root ``data-fill-panel`` (the start
            // page) fills the panel's height, so it can push a footnote to
            // the bottom. Only then is the panel a flex column: other pages
            // centre themselves with auto margins, which would shrink them
            // to their content in a flex column.
            "&:has(> [data-fill-panel])": { display: "flex", flexDirection: "column" },
            // Phones keep the panel's inner margin small, so tables and
            // the composer get almost the full width.
            p: { xs: 1.5, md: `${shell.panelPadding}px` },
            overflow: "auto",
            minWidth: 0,
            minHeight: 0,
          }}
        >
          <MaintenanceBanner />
          <WhatsNewController />
          {feedbackOpen && feedbackConfig.data && (
            <FeedbackDialog
              config={feedbackConfig.data}
              onClose={() => setFeedbackOpen(false)}
            />
          )}
          {requestsOn && (
            <AskPanel
              open={ask.open}
              tab={ask.tab}
              onTab={ask.setTab}
              onClose={ask.close}
              feedbackConfig={feedbackConfig.data}
            />
          )}
          <OnboardingAutoShow />
          {isMobile && <OnboardingNudge inPanel sx={{ mb: 1.5 }} />}
          <AddonMemberNotice
            slug={support?.org_slug ?? me.data?.admin_scope?.active_org_slug ?? null}
            pending={support?.member_notice_pending ?? false}
            notice={support?.member_notice}
          />
          <Outlet />
        </Box>
        {/* The right column: a page (the session view) portals its
            panels in here; hidden until one claims it. */}
        <ShellRightColumn claimed={rightColumnClaimed} ref={setRightColumnHost} />
      </Box>
    </Box>
    </RightColumnContext.Provider>
  );
}

/** Compact org switcher in the AppBar. Visible whenever the user
 *  has 2+ orgs to choose between. Hidden for users with exactly one —
 *  there's nothing to choose, the dropdown would only add noise (in
 *  admin, the page header's scope chip still names their organisation).
 *
 *  The one organisation picker for the whole app, the admin area
 *  included. The admin menu used to carry its own (AdminOrgPicker) and
 *  hide this one there: the selector moved between the top bar and the
 *  left column as you went in and out of admin. Pages that can't show
 *  "All organisations" still put AdminOrgPicker inline, in place of
 *  their content, until one is picked. */
function AppBarOrgSwitcher(): React.JSX.Element | null {
  const { t } = useTranslation();
  const location = useLocation();
  const { slug, setSlug, orgs, isPersisting, isLoading, canSeeAllOrgs } =
    useActiveOrg();
  // One request, for the ACTIVE org only — a per-row fetch would cost
  // one request per listed org for a decoration nobody reads while the
  // menu is closed.
  const activeBrand = useActiveOrgBrand(slug ?? null);
  const autoPinnedSlugRef = React.useRef<string | null>(null);

  // "All organisations" only in admin: dfac830b removed the sentinel
  // from app pages because it made new-session context an ambiguous
  // cross-org choice. The admin area's platform-wide pages are cross-org
  // by definition; there it clears the pin, as AdminOrgPicker does.
  const onAdminPage = isAdminPath(location.pathname);
  const offerAll = onAdminPage && canSeeAllOrgs;

  // Auto-pin exists so non-admin pages (sessions, documents) always have a
  // concrete org. Pin to an org the user is actually a MEMBER of — for a
  // platform admin ``orgs`` also contains orgs they don't belong to, and
  // pinning them into someone else's tenant (or the empty root org) is never
  // what they meant. A platform admin with no membership stays unpinned,
  // which is the "All organisations" view.
  const firstMemberOrgSlug =
    orgs.find((o) => o.membership_role != null)?.slug ?? "";
  const firstOrgSlug = canSeeAllOrgs ? firstMemberOrgSlug : orgs[0]?.slug ?? "";
  const shouldAutoPinConcreteOrg =
    !onAdminPage &&
    !slug &&
    Boolean(firstOrgSlug) &&
    !isPersisting;

  React.useEffect(() => {
    // "All organisations" picked in admin clears the pin; leaving admin
    // must pin again, even to the org pinned last time.
    if (onAdminPage) autoPinnedSlugRef.current = null;
  }, [onAdminPage]);

  React.useEffect(() => {
    if (!shouldAutoPinConcreteOrg) return;
    if (autoPinnedSlugRef.current === firstOrgSlug) return;
    autoPinnedSlugRef.current = firstOrgSlug;
    setSlug(firstOrgSlug);
  }, [firstOrgSlug, setSlug, shouldAutoPinConcreteOrg]);

  if (isLoading || orgs.length === 0) return null;
  if (orgs.length === 1 && !offerAll) return null;

  // In admin the root organisation says so, as the admin pages do.
  const nameOf = (o: (typeof orgs)[number]) => (onAdminPage ? orgDisplayName(t, o) : o.name);
  const value = slug ?? (offerAll ? ALL_ORGS : "");
  const picked = orgs.find((o) => o.slug === value);
  const current =
    value === ALL_ORGS
      ? t("admin.org_switcher.all")
      : picked
        ? nameOf(picked)
        : t("appbar.org_switcher.select", "Select organisation");

  return (
    <FormControl
      size="small"
      sx={{
        // Compact on phones instead of hidden: a multi-org member on
        // a phone had NO way to switch orgs (the switcher was the
        // only affordance). Narrow width keeps the AppBar usable.
        minWidth: { xs: 110, md: 190 },
        maxWidth: { xs: 136, md: "none" },
        display: "inline-flex",
        "& .MuiOutlinedInput-root": {
          color: "inherit",
          "& fieldset": { borderColor: "rgba(255,255,255,0.4)" },
          "&:hover fieldset": { borderColor: "rgba(255,255,255,0.6)" },
        },
        "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.7)" },
        "& .MuiSelect-icon": { color: "inherit" },
      }}
      disabled={isPersisting}
    >
      <InputLabel id="appbar-org-switcher-label">
        {t("appbar.org_switcher.label", "Org")}
      </InputLabel>
      {/* Read only through aria-labelledby (hidden text still counts
          there): a logo mark alone would leave the value unnamed. */}
      <Box id="appbar-org-switcher-current" hidden>
        {current}
      </Box>
      <Select
        labelId="appbar-org-switcher-label"
        // The active org joins the label in the accessible name ("Org
        // Acme"); "Org" alone didn't say which organisation is active.
        SelectDisplayProps={{
          "aria-labelledby": "appbar-org-switcher-label appbar-org-switcher-current",
        } as React.HTMLAttributes<HTMLDivElement>}
        value={value}
        label={t("appbar.org_switcher.label", "Org")}
        renderValue={(v) => {
          if (v === ALL_ORGS) {
            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                <ApartmentOutlined fontSize="small" />
                <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                  {current}
                </Box>
              </Box>
            );
          }
          if (!picked) return t("appbar.org_switcher.select", "Select organisation");
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
              <OrgBrandMark name={picked.name} brand={activeBrand} />
              {/* A wordmark logo already says the name — showing both
                  is what made this cramped. */}
              {!activeBrand.logoUrl && (
                <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                  {nameOf(picked)}
                </Box>
              )}
            </Box>
          );
        }}
        onChange={(e) => {
          const next = String(e.target.value);
          if (next === ALL_ORGS) setSlug(null);
          else if (next) setSlug(next);
        }}
      >
        {offerAll ? (
          <MenuItem value={ALL_ORGS}>{t("admin.org_switcher.all")}</MenuItem>
        ) : (
          <MenuItem value="" disabled>
            {t("appbar.org_switcher.select", "Select organisation")}
          </MenuItem>
        )}
        {orgs.map((o) => (
          <MenuItem key={o.slug} value={o.slug}>
            {nameOf(o)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

/** The active org's identity in the switcher: its logo when it has
 *  one, else a swatch of its brand colour, else its initial.
 *
 *  Contrast-gated: a tenant picks its own colour, so a swatch that
 *  would vanish into the AppBar (a navy brand on the navy bar, a white
 *  one on a light bar) gets an outline, and the initial inside it is
 *  drawn in whichever of black/white actually reads on that colour.
 *  Without the gate a tenant can configure itself an invisible mark.
 */
export function OrgBrandMark({
  name,
  brand,
}: {
  name: string;
  brand: { color: string | null; logoUrl: string | null };
}): React.JSX.Element {
  // The swatch sits on the page behind the transparent app bar, not on
  // the white palette primary — measuring against the wrong ground left
  // near-black brand colours invisible.
  const surface = repoTokens.colors.page;
  if (brand.logoUrl) {
    // ``objectFit: contain`` is what actually guarantees the aspect
    // ratio: with a bare height + maxWidth, a wide wordmark gets
    // SQUASHED horizontally into the width cap. Sized to stay legible
    // (a 40px-wide wordmark was an unreadable smudge) without letting a
    // very wide logo dominate the AppBar — an over-wide one scales down
    // proportionally inside the box instead of stretching it.
    return (
      <Box
        component="img"
        src={brand.logoUrl}
        // The logo IS the org's name here (these are wordmarks), so it
        // replaces the text label rather than sitting next to a copy of
        // it. Keeps the accessible name for screen readers + hover, and
        // the dropdown still lists every org by name.
        alt={name}
        title={name}
        sx={{
          height: 22,
          width: "auto",
          maxWidth: 132,
          objectFit: "contain",
          display: "block",
          flexShrink: 0,
        }}
      />
    );
  }
  const color = brand.color;
  const initial = (name.trim()[0] ?? "?").toUpperCase();
  return (
    <Box
      aria-hidden
      sx={{
        flexShrink: 0,
        width: 18,
        height: 18,
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        fontSize: 10,
        fontWeight: 700,
        lineHeight: 1,
        bgcolor: color ?? "rgba(255,255,255,0.18)",
        color: color ? onColor(color) : "inherit",
        border:
          color && needsRing(color, surface)
            ? "1px solid rgba(255,255,255,0.75)"
            : "1px solid transparent",
      }}
    >
      {initial}
    </Box>
  );
}
